import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../../api/client'

interface Instrument {
  code: string
  status: 'pending' | 'scheduled' | 'answered' | 'expired' | 'revoked' | 'used' | 'awaiting_date'
  available_from: string | null
  expires_at: string | null
  answered_at: string | null
}
export interface StudyScheduleView {
  active: boolean
  version: string
  can_write: boolean
  intervention_ended_at: string | null
  legacy_invitations: number
  phases: { phase: string; label: string; instruments: Instrument[] }[]
}
const statusLabels: Record<Instrument['status'], string> = {
  pending: 'Por responder', scheduled: 'Agendada', answered: 'Respondida',
  expired: 'Ventana vencida', revoked: 'Revocada', used: 'Respondida',
  awaiting_date: 'Espera el fin de la intervención',
}
const dateLabel = (value: string) => new Date(value).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })
function failure(error: unknown) {
  if (error instanceof ApiError && error.body && typeof error.body === 'object' && 'detail' in error.body && typeof error.body.detail === 'string') return error.body.detail
  return error instanceof Error ? error.message : 'No se pudo actualizar la agenda.'
}

/** Shared by the manager's player detail and the administrator's timeline. */
export function StudySchedulePanel({ userId, clubId }: { userId: number; clubId?: number }) {
  const path = clubId === undefined ? `/admin/users/${userId}/study-schedule` : `/club/${clubId}/players/${userId}/study-schedule`
  const currentPath = useRef(path)
  currentPath.current = path
  const [data, setData] = useState<StudyScheduleView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [endedAt, setEndedAt] = useState('')
  const load = useCallback(async () => {
    setError(null)
    try {
      const result = await api.get<StudyScheduleView>(path)
      if (currentPath.current === path) setData(result)
    } catch (e) { if (currentPath.current === path) setError(failure(e)) }
  }, [path])
  useEffect(() => { setData(null); setEndedAt(''); setBusy(false); void load() }, [load])
  const save = async (completion: boolean) => {
    if (busy) return
    setBusy(true); setError(null)
    try {
      const payload = completion ? { ended_at: new Date(endedAt).toISOString() } : {}
      const result = await api.post<StudyScheduleView>(completion ? `${path}/completion` : path, payload)
      if (currentPath.current === path) setData(result)
    } catch (e) { if (currentPath.current === path) setError(failure(e)) }
    finally { if (currentPath.current === path) setBusy(false) }
  }
  const baselineComplete = data?.phases[0]?.instruments.every(i => i.status === 'answered') ?? false
  return <section className="card mb-lg" aria-label="Agenda del estudio">
    <h2 className="mt-0">Agenda del estudio</h2>
    {error && <div className="banner-err" role="alert">{error} <button className="btn-ghost" disabled={busy} onClick={() => void load()}>Reintentar consulta</button></div>}
    {!data && !error && <p>Cargando agenda…</p>}
    {data && <>
      {data.legacy_invitations > 0 && <p className="text-sm text-text-muted">Hay invitaciones del calendario anterior. Se conservan para revisión; esta agenda no modifica sus respuestas ni sus fechas.</p>}
      {!data.active ? <>
        <p>Este jugador todavía no tiene la agenda actual. Comienza con SMTQ y CSAI-2R antes de entrenar.</p>
        {data.can_write && <button className="btn-primary" disabled={busy} onClick={() => void save(false)}>{busy ? 'Iniciando…' : 'Iniciar agenda del estudio'}</button>}
      </> : <>
        <p className="text-sm text-text-muted">La evaluación posterior abre al finalizar la intervención y debe completarse dentro de las 48 horas. El seguimiento abre tres meses después de esa fecha.</p>
        <div className="table-wrap"><table><thead><tr><th>Evaluación</th><th>Cuestionario</th><th>Estado</th><th>Fechas</th></tr></thead><tbody>
          {data.phases.flatMap(phase => phase.instruments.map(instrument => <tr key={`${phase.phase}-${instrument.code}`}>
            <td>{phase.label}</td><td>{instrument.code}</td><td>{statusLabels[instrument.status]}</td>
            <td className="text-sm">{instrument.answered_at ? dateLabel(instrument.answered_at) : instrument.available_from ? <>
              Desde {dateLabel(instrument.available_from)}{instrument.expires_at && <> · Hasta {dateLabel(instrument.expires_at)}</>}
            </> : 'Aún sin fecha'}</td>
          </tr>))}
        </tbody></table></div>
        {data.intervention_ended_at ? <p>Fin de intervención registrado: <strong>{dateLabel(data.intervention_ended_at)}</strong>.</p> : data.can_write && <form className="mt-md" onSubmit={event => { event.preventDefault(); void save(true) }}>
          <p>Registrá el fin real cuando haya terminado toda la intervención del jugador. Esta fecha fija las dos evaluaciones restantes.</p>
          {!baselineComplete && <p className="text-sm text-text-muted">Primero debe completar la evaluación inicial.</p>}
          <label className="block text-sm" htmlFor={`study-end-${userId}`}>Fecha y hora del último encuentro</label>
          <input id={`study-end-${userId}`} className="input mb-sm" type="datetime-local" required value={endedAt} onChange={event => setEndedAt(event.target.value)} disabled={busy || !baselineComplete} />
          <button className="btn-primary block" type="submit" disabled={busy || !baselineComplete || !endedAt}>{busy ? 'Guardando…' : 'Registrar fin de intervención'}</button>
        </form>}
      </>}
    </>}
  </section>
}
